/**
 * Product Specifications Section
 * Handles smooth transitions between mobile and desktop table views
 * to prevent flickering during window resize
 */
(function() {
  'use strict';

  class ProductSpecifications {
    constructor(element) {
      this.element = element;
      this.resizeTimeout = null;
      this.lastWidth = window.innerWidth;
      this.isTransitioning = false;
      this.animationFrameId = null;
      this.desktopTable = null;
      this.mobileTable = null;
    }

    init() {
      this.desktopTable = this.element.querySelector('.specification-table-desktop');
      this.mobileTable = this.element.querySelector('.specification-table-mobile');
      
      if (!this.desktopTable || !this.mobileTable) return;
      
      // Set initial state based on current width
      this.updateTableVisibility();
      
      // Use ResizeObserver for more efficient resize detection
      // It only fires when the element's size actually changes
      if (window.ResizeObserver) {
        this.resizeObserver = new ResizeObserver((entries) => {
          // Cancel any pending animation frame
          if (this.animationFrameId) {
            cancelAnimationFrame(this.animationFrameId);
          }
          
          // Use requestAnimationFrame to batch updates
          this.animationFrameId = requestAnimationFrame(() => {
            const width = entries[0].contentRect.width;
            // Only update if width actually changed significantly (avoid sub-pixel changes)
            if (Math.abs(width - this.lastWidth) > 1) {
              this.lastWidth = width;
              this.updateTableVisibility();
            }
          });
        });
        
        // Observe the container, not the window
        this.resizeObserver.observe(this.element);
      } else {
        // Fallback to window resize event with debouncing
        this.handleResize = this.debouncedResize.bind(this);
        window.addEventListener('resize', this.handleResize, { passive: true });
      }
    }

    destroy() {
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      } else if (this.handleResize) {
        window.removeEventListener('resize', this.handleResize);
      }
      
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }
      
      if (this.animationFrameId) {
        cancelAnimationFrame(this.animationFrameId);
      }
    }

    debouncedResize() {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      // Debounce with a small delay to avoid excessive updates
      this.resizeTimeout = setTimeout(() => {
        const currentWidth = window.innerWidth;
        // Only update if width actually changed significantly
        if (Math.abs(currentWidth - this.lastWidth) > 1) {
          this.lastWidth = currentWidth;
          this.updateTableVisibility();
        }
      }, 16); // ~60fps
    }

    updateTableVisibility() {
      if (this.isTransitioning || !this.desktopTable || !this.mobileTable) return;

      const isMobile = window.innerWidth <= 749;
      const currentDesktopDisplay = window.getComputedStyle(this.desktopTable).display;
      const currentMobileDisplay = window.getComputedStyle(this.mobileTable).display;
      
      // Only update if state actually needs to change
      const shouldShowMobile = isMobile && currentMobileDisplay === 'none';
      const shouldShowDesktop = !isMobile && currentDesktopDisplay === 'none';
      
      if (!shouldShowMobile && !shouldShowDesktop) return;
      
      this.isTransitioning = true;
      
      // Use requestAnimationFrame for smooth, batched updates
      requestAnimationFrame(() => {
        if (isMobile) {
          // Switch to mobile: hide desktop first, then show mobile
          this.desktopTable.style.display = 'none';
          // Use a second frame to ensure desktop is hidden before showing mobile
          requestAnimationFrame(() => {
            this.mobileTable.style.display = 'table';
            this.isTransitioning = false;
          });
        } else {
          // Switch to desktop: hide mobile first, then show desktop
          this.mobileTable.style.display = 'none';
          // Use a second frame to ensure mobile is hidden before showing desktop
          requestAnimationFrame(() => {
            this.desktopTable.style.display = 'table';
            this.isTransitioning = false;
          });
        }
      });
    }
  }

  // Initialize on DOM ready
  function initProductSpecifications() {
    const specsElements = document.querySelectorAll('.product-specifications');
    const instances = [];
    
    specsElements.forEach((element) => {
      // Skip if already initialized
      if (element.dataset.specsInitialized) return;
      
      const instance = new ProductSpecifications(element);
      instance.init();
      element.dataset.specsInitialized = 'true';
      instances.push(instance);
    });
    
    return instances;
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initProductSpecifications);
  } else {
    initProductSpecifications();
  }

  // Re-initialize for dynamically loaded content (e.g., theme editor)
  if (typeof document.addEventListener !== 'undefined') {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.detail.sectionId;
      if (section && section.includes('product-specifications')) {
        initProductSpecifications();
      }
    });
  }
})();
